import { translate } from "app/filters/i18nFilter";

import { GROWTH_OVERVIEW_CATEGORY_PAGE_SIZE, GrowthOverviewGranularity, GrowthOverviewMetric } from "./const";

export const GROWTH_OVERVIEW_I18N_KEYS = {
    title: "market.segmentation.growth.overview.title",
    titleTooltip: "market.segmentation.growth.overview.title.tooltip",
    showMoreDomains: "market.segmentation.growth.overview.show.more.domains",
    showMoreIndustries: "market.segmentation.growth.overview.show.more.industries",
    showMoreSubIndustries: "market.segmentation.growth.overview.show.more.sub.industries",
    searchPlaceholder: "common.placeholder.search",
    metric: {
        visits: "market.segmentation.growth.overview.metric.visits",
        unique: "market.segmentation.growth.overview.metric.unique",
    },
    granularity: {
        weekly: "market.segmentation.growth.overview.granularity.weekly",
        monthly: "market.segmentation.growth.overview.granularity.monthly",
        quarterly: "market.segmentation.growth.overview.granularity.quarterly",
        quarterlyDisabled: "market.segmentation.growth.overview.granularity.quarterly.disabled.tooltip",
        quarterlyDisabledUnique: "market.segmentation.growth.overview.granularity.quarterly.disabled.unique.tooltip",
    },
    column: {
        summaryVisits: "market.segmentation.growth.overview.column.summary.visits",
        summaryVisitsWeekly: "market.segmentation.growth.overview.column.summary.visits.weekly",
        summaryVisitsQuarterly: "market.segmentation.growth.overview.column.summary.visits.quarterly",
        summaryUnique: "market.segmentation.growth.overview.column.summary.unique",
        summaryUniqueWeekly: "market.segmentation.growth.overview.column.summary.unique.weekly",
        summaryUniqueQuarterly: "market.segmentation.growth.overview.column.summary.unique.quarterly",
        domains: "market.segmentation.growth.overview.column.domains",
        domainsCount: "market.segmentation.growth.overview.column.domains.count",
        industry: "market.segmentation.growth.overview.column.industry",
        industryCount: "market.segmentation.growth.overview.column.industry.count",
        subIndustry: "market.segmentation.growth.overview.column.sub.industry",
        subIndustryCount: "market.segmentation.growth.overview.column.sub.industry.count",
        incompleteSuffix: "market.segmentation.growth.overview.column.incomplete.suffix",
    },
    tooltip: {
        visitsUnit: "market.segmentation.growth.overview.tooltip.visits.unit",
        uniqueUnit: "market.segmentation.growth.overview.tooltip.unique.unit",
        incompleteQuarter: "market.segmentation.growth.overview.tooltip.incomplete.quarter",
        incompleteWeek: "market.segmentation.growth.overview.tooltip.incomplete.week",
        categoryWeeklyUniqueUnavailable:
            "market.segmentation.growth.overview.tooltip.category.weekly.unique.unavailable",
    },
    noData: {
        title: "market.segmentation.growth.overview.no.data.title",
        subtitle: "market.segmentation.growth.overview.no.data.subtitle",
    },
    error: {
        title: "market.segmentation.growth.overview.error.title",
        subtitle: "market.segmentation.growth.overview.error.subtitle",
    },
} as const;

export type TGrowthOverviewColumnLevel = "industries" | "subIndustries" | "domains";

const GROWTH_OVERVIEW_COLUMN_KEYS = {
    domains: {
        empty: GROWTH_OVERVIEW_I18N_KEYS.column.domains,
        count: GROWTH_OVERVIEW_I18N_KEYS.column.domainsCount,
    },
    industries: {
        empty: GROWTH_OVERVIEW_I18N_KEYS.column.industry,
        count: GROWTH_OVERVIEW_I18N_KEYS.column.industryCount,
    },
    subIndustries: {
        empty: GROWTH_OVERVIEW_I18N_KEYS.column.subIndustry,
        count: GROWTH_OVERVIEW_I18N_KEYS.column.subIndustryCount,
    },
} as const;

export const getGrowthOverviewShowMoreDomainsLabel = (remainingCount: number): string =>
    translate(GROWTH_OVERVIEW_I18N_KEYS.showMoreDomains, { count: remainingCount }, `Show ${remainingCount} more`);

export const getGrowthOverviewShowMoreIndustriesLabel = (remainingCount: number): string => {
    const batchSize = Math.min(remainingCount, GROWTH_OVERVIEW_CATEGORY_PAGE_SIZE);

    return translate(
        GROWTH_OVERVIEW_I18N_KEYS.showMoreIndustries,
        { count: batchSize },
        `Show ${batchSize} more industries`,
    );
};

export const getGrowthOverviewShowMoreSubIndustriesLabel = (remainingCount: number): string => {
    const batchSize = Math.min(remainingCount, GROWTH_OVERVIEW_CATEGORY_PAGE_SIZE);

    return translate(
        GROWTH_OVERVIEW_I18N_KEYS.showMoreSubIndustries,
        { count: batchSize },
        `Show ${batchSize} more sub-industries`,
    );
};

export const getGrowthOverviewColumnNameKey = (count: number, level: TGrowthOverviewColumnLevel): string => {
    const keys = GROWTH_OVERVIEW_COLUMN_KEYS[level];
    return count > 0 ? keys.count : keys.empty;
};

export const GROWTH_OVERVIEW_SUMMARY_COLUMN_KEYS = {
    [GrowthOverviewMetric.Visits]: {
        [GrowthOverviewGranularity.Weekly]: GROWTH_OVERVIEW_I18N_KEYS.column.summaryVisitsWeekly,
        [GrowthOverviewGranularity.Monthly]: GROWTH_OVERVIEW_I18N_KEYS.column.summaryVisits,
        [GrowthOverviewGranularity.Quarterly]: GROWTH_OVERVIEW_I18N_KEYS.column.summaryVisitsQuarterly,
    },
    [GrowthOverviewMetric.Unique]: {
        [GrowthOverviewGranularity.Weekly]: GROWTH_OVERVIEW_I18N_KEYS.column.summaryUniqueWeekly,
        [GrowthOverviewGranularity.Monthly]: GROWTH_OVERVIEW_I18N_KEYS.column.summaryUnique,
        [GrowthOverviewGranularity.Quarterly]: GROWTH_OVERVIEW_I18N_KEYS.column.summaryUniqueQuarterly,
    },
} as const;

export const getGrowthOverviewSummaryColumnKey = (
    metric: GrowthOverviewMetric,
    granularity: GrowthOverviewGranularity,
): string => GROWTH_OVERVIEW_SUMMARY_COLUMN_KEYS[metric][granularity];

export const GROWTH_OVERVIEW_METRIC_LABEL_KEYS = {
    [GrowthOverviewMetric.Visits]: GROWTH_OVERVIEW_I18N_KEYS.metric.visits,
    [GrowthOverviewMetric.Unique]: GROWTH_OVERVIEW_I18N_KEYS.metric.unique,
} as const;

export const GROWTH_OVERVIEW_TOOLTIP_UNIT_KEYS = {
    [GrowthOverviewMetric.Visits]: GROWTH_OVERVIEW_I18N_KEYS.tooltip.visitsUnit,
    [GrowthOverviewMetric.Unique]: GROWTH_OVERVIEW_I18N_KEYS.tooltip.uniqueUnit,
} as const;

export const GROWTH_OVERVIEW_INCOMPLETE_PERIOD_TOOLTIP_KEYS = {
    [GrowthOverviewGranularity.Weekly]: GROWTH_OVERVIEW_I18N_KEYS.tooltip.incompleteWeek,
    [GrowthOverviewGranularity.Quarterly]: GROWTH_OVERVIEW_I18N_KEYS.tooltip.incompleteQuarter,
} as const;
